import { toast } from "@/components/ui/use-toast";
import { handleApiError } from "@/lib/api/error";
import { defaultNS, useTranslation } from "@/lib/i18n";
import { FIVE_MINUTES, remainingTimeToday } from "@/lib/query";
import { queryClient } from "@/lib/queryClient";
import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import type { FileWithPath } from "react-dropzone";
import {
  deactivateAppIntegration,
  deleteAppIntegration,
  deleteCustomAppUser,
  deleteSoftwareLogo,
  getAppIntegrationSettings,
  getCountByCategories,
  getPresignedUrl,
  getRefreshResourcesStatus,
  getSoftware,
  getSoftwareLogoPresignedUrl,
  getSoftwareOverviewBillingCost,
  getSoftwareOverviewMonthlyActiveUserRate,
  getSoftwareOverviewMonthlyCost,
  getSoftwareOverviewRecommendedActions,
  getSoftwareOverviewRenewalDate,
  getSoftwareOverviewSavingCost,
  getSoftwareOverviewUser,
  getSoftwareOverviewUserGrowth,
  getSoftwareUsers,
  patchExternalTenantSettings,
  postRefreshResources,
  reAuthenticate,
  reactivateAppIntegration,
  updateAppUserProvisioning,
  updateCustomApp,
  updateCustomAppContract,
  updatePricingPlan,
  updateSoftwareArchitecture,
  updateSoftwareDetail,
  uploadSoftwareLogo,
} from "./api";
import { softwareKey } from "./key";
import { convertToUsersTableRow } from "./selector";
import type {
  AppIntegrationSettingsRes,
  CustomAppContractState,
  CustomAppState,
  GetAppIntegrationUsersParams,
  PatchExternalTenantSettingsReq,
  PricingPlanState,
  ReAuthenticateReq,
  ReAuthenticateRes,
  UpdateSoftwareArchitectureReq,
  UpdateSoftwareDetailReq,
} from "./type";

export const useRefreshResourcesStatus = (id: string) => {
  return useQuery({
    queryKey: softwareKey.refreshStatus(id),
    queryFn: async () => getRefreshResourcesStatus(id),
    refetchOnWindowFocus: true,
    staleTime: 0,
    refetchInterval: (query) =>
      query.state.data?.status === "IN_PROGRESS" ? 5000 : false,
  });
};

export const usePostRefreshResources = (id: string) => {
  const { i18n, t } = useTranslation();
  return useMutation({
    mutationFn: async () => {
      const res = await postRefreshResources(id);
      return res;
    },
    onSuccess: (data) => {
      const messages: Record<typeof data.status, string> = {
        ENQUEUED: t("pages.apps.integrations.toast.refresh.triggered"),
        ALREADY_IN_PROGRESS: t(
          "pages.apps.integrations.toast.refresh.alreadyInProgress",
        ),
        RATE_LIMITED: t("pages.apps.integrations.toast.refresh.rateLimited"),
      } as const;
      toast({ description: messages[data.status] });
      queryClient.invalidateQueries({
        queryKey: softwareKey.refreshStatus(id),
      });
    },
    onError: (error) => {
      const { message } = handleApiError(error, { language: i18n.language });
      toast({ description: message, variant: "destructive" });
    },
  });
};

export const useSoftwareUsers = (
  id: string,
  search: GetAppIntegrationUsersParams,
) => {
  return useQuery({
    queryKey: softwareKey.usersSearch(id, search),
    queryFn: () => getSoftwareUsers(id, search),
    select: (data) => ({
      users: convertToUsersTableRow(data),
      total: data.length,
      count: data.count,
      pageIndex: data.page,
      maxPage: data.maxPage,
    }),
  });
};

export const useSoftware = (id: string) => {
  return useSuspenseQuery({
    queryKey: softwareKey.contract(id),
    queryFn: () => getSoftware(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewUserQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewUser(id),
    queryFn: () => getSoftwareOverviewUser(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewBillingCostQuery = (id: string) => {
  return useSuspenseQuery({
    queryKey: softwareKey.overviewBillingCost(id),
    queryFn: () => getSoftwareOverviewBillingCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewUserGrowthQuery = (id: string) => {
  return useSuspenseQuery({
    queryKey: softwareKey.overviewUserGrowth(id),
    queryFn: () => getSoftwareOverviewUserGrowth(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewMonthlyActiveUserRateQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewMonthlyActiveUserRate(id),
    queryFn: () => getSoftwareOverviewMonthlyActiveUserRate(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewMonthlyCostQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewMonthlyCost(id),
    queryFn: () => getSoftwareOverviewMonthlyCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewRenewalDateQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewRenewalDate(id),
    queryFn: () => getSoftwareOverviewRenewalDate(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewSavingCostQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewSavingCost(id),
    queryFn: () => getSoftwareOverviewSavingCost(id),
    staleTime: remainingTimeToday(),
  });
};

export const useSoftwareOverviewRecommendedActionsQuery = (id: string) => {
  return useQuery({
    queryKey: softwareKey.overviewRecommendedActions(id),
    queryFn: () => getSoftwareOverviewRecommendedActions(id),
    staleTime: remainingTimeToday(),
  });
};

export const useCountByCategories = (id: string) => {
  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: softwareKey.countByCategories(id),
    queryFn: () => getCountByCategories(id),
    staleTime: FIVE_MINUTES,
  });
  return { data, loading, error };
};

export const useAppIntegrationSettings = (id: string) => {
  return useSuspenseQuery({
    queryKey: softwareKey.setting(id),
    queryFn: () => getAppIntegrationSettings(id),
  });
};

export const invalidateSoftware = (id: string) => {
  return queryClient.invalidateQueries({
    queryKey: softwareKey.all(id),
  });
};

export const useTenantMutator = (id: string) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: async (tenantData: PatchExternalTenantSettingsReq) => {
      await patchExternalTenantSettings(id, tenantData);
    },
    onSuccess: () => {
      invalidateSoftware(id);
      toast({ title: "Success!!" });
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useDeactivateIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deactivateAppIntegration(id),
    onSuccess: () => {
      invalidateSoftware(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useDeleteIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteAppIntegration(id),
    onSuccess: () => {
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useReactivateIntegrationMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => reactivateAppIntegration(id),
    onSuccess: () => {
      invalidateSoftware(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUpdateCustomAppMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (appState: CustomAppState) => {
      return updateCustomApp(id, appState);
    },
    onSuccess: () => {
      invalidateSoftware(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUploadImageMutator = (options?: {
  onSuccess?: (data: { key: string }) => void;
  onError?: (message: string) => void;
}) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: async (file: FileWithPath) => {
      const data = await getPresignedUrl(file.name);
      if (data) {
        await fetch(data.url, {
          method: "PUT",
          body: file,
          headers: {
            "Content-Type": file.type,
          },
        });
        return data;
      }
      throw new Error("Failed to get presigned URL");
    },
    onSuccess: (data) => {
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUpdateCustomAppContractMutator = (
  id: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (body: CustomAppContractState) => {
      return updateCustomAppContract(id, body);
    },
    onSuccess: () => {
      invalidateSoftware(id);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const usePricingPlanMutator = (
  appIntegrationId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: (pricingData: PricingPlanState) => {
      return updatePricingPlan(appIntegrationId, pricingData);
    },
    onSuccess: () => {
      invalidateSoftware(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useAppUserProvisioningMutator = (
  id: string,
  options?: {
    isEnabled: boolean;
    onSuccess?: (data: AppIntegrationSettingsRes) => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate } = useMutation({
    mutationFn: () => {
      return updateAppUserProvisioning(id, {
        USER_PROVISIONING: options?.isEnabled ?? false,
      });
    },
    onSuccess: (data) => {
      invalidateSoftware(id);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate };
};

export const useReAuthenticateMutator = (
  appIntegrationId: string,
  options?: {
    onSuccess?: (data: ReAuthenticateRes) => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: ReAuthenticateReq) => {
      return reAuthenticate(appIntegrationId, data);
    },
    onSuccess: (data) => {
      invalidateSoftware(appIntegrationId);
      options?.onSuccess?.(data);
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useDeleteAppUserMutator = (
  appIntegrationId: string,
  appUserId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();

  const { mutate, isPending } = useMutation({
    mutationFn: () => deleteCustomAppUser(appIntegrationId, appUserId),
    onSuccess: () => {
      invalidateSoftware(appIntegrationId);
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      options?.onError?.(message);
      toast({ title: message });
    },
  });

  return { mutate, isPending };
};

export const useUpdateSoftwareDetailMutator = (
  softwareId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software",
  });

  return useMutation({
    mutationFn: async (
      data: UpdateSoftwareDetailReq & {
        logoFile?: File | null;
        logoChanged?: boolean;
        logoDeleted?: boolean;
      },
    ) => {
      // まず基本情報を更新
      const result = await updateSoftwareDetail(softwareId, data);

      // ロゴの処理
      if (data.logoChanged || data.logoDeleted) {
        const presignedUrlData = await getSoftwareLogoPresignedUrl(softwareId);

        if (data.logoDeleted) {
          // ロゴを削除
          await deleteSoftwareLogo(presignedUrlData.url);
        } else if (data.logoFile) {
          // 新しいロゴをアップロード
          await uploadSoftwareLogo(presignedUrlData.url, data.logoFile);
        }
      }

      return result;
    },
    onSuccess: () => {
      invalidateSoftware(softwareId);
      toast({ description: t("edit.toast.updateSuccess") });
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ description: message, variant: "destructive" });
      options?.onError?.(message);
    },
  });
};

export const useUpdateSoftwareArchitectureMutator = (
  softwareId: string,
  options?: {
    onSuccess?: () => void;
    onError?: (message: string) => void;
  },
) => {
  const { i18n } = useTranslation();
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.software",
  });

  return useMutation({
    mutationFn: (data: UpdateSoftwareArchitectureReq) =>
      updateSoftwareArchitecture(softwareId, data),
    onSuccess: () => {
      invalidateSoftware(softwareId);
      toast({ description: t("edit.toast.updateSuccess") });
      options?.onSuccess?.();
    },
    onError: (error) => {
      const { message } = handleApiError(error, {
        language: i18n.language,
      });
      toast({ description: message, variant: "destructive" });
      options?.onError?.(message);
    },
  });
};
